import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageProvider';
import { getApiUrl } from '../lib/api';

/*
 * «Avísame cuando abra» (13-oct-2026).
 *
 * Mientras el cobro está pausado (COBRO_ACTIVO=false en src/lib/cobro.js y sin
 * COBRO_ACTIVO=true en Railway), los botones de pago no llevan a Stripe ni muestran un
 * error: recogen el correo de quien quiere que le avisemos el día que abran los planes.
 *
 * RGPD: la casilla viene SIN marcar, enlaza a la política de privacidad y el servidor
 * (/api/avisame) guarda el consentimiento con fecha y origen en Brevo, el mismo sistema de
 * leads que ya usa el formulario de resultado. Si Brevo no está configurado, el servidor
 * responde 503 y aquí se dice que no se ha guardado: nunca «hecho» sin haberlo hecho.
 *
 *  - origen: desde qué pantalla se apunta ('precios' | 'resultado').
 *  - plan: qué plan le interesa ('trabajador' | 'asesoria' | 'cualquiera').
 *  - compacto: versión reducida para meterla dentro de una tarjeta.
 */
export default function AvisameForm({ origen = 'otro', plan = 'cualquiera', compacto = false, onGuardado, onCerrar }) {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [web, setWeb] = useState(''); // campo trampa para robots: una persona no lo ve ni lo rellena
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [guardado, setGuardado] = useState(false);

  const idEmail = `avisame-email-${origen}-${plan}`;
  const idConsent = `avisame-consent-${origen}-${plan}`;

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setError(t('avisame.errEmail')); return; }
    if (!consent) { setError(t('avisame.errConsent')); return; }
    setLoading(true);
    try {
      await axios.post(`${getApiUrl()}/api/avisame`, { email, consent: true, origen, plan, web }, { timeout: 15000 });
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.error) || t('avisame.errGuardar');
      setError(msg);
      setLoading(false);
      return;
    }
    setLoading(false);
    setGuardado(true);
    if (onGuardado) onGuardado(email);
  }

  if (guardado) {
    return (
      <div className="rounded-2xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/40 p-4 text-left" role="status">
        <p className="font-bold text-green-800 dark:text-green-300">{t('avisame.okTitulo')}</p>
        <p className="text-sm text-green-800/90 dark:text-green-200/90 mt-1">{t('avisame.okTexto').replace('{email}', email)}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="avisame-form" className={`text-left ${compacto ? 'space-y-3' : 'space-y-4'}`} noValidate>
      {!compacto && <p className="text-sm text-gray-600 dark:text-gray-300">{t('avisame.lead')}</p>}
      <div>
        <label htmlFor={idEmail} className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">{t('avisame.emailLabel')}</label>
        <input id={idEmail} type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder={t('avisame.emailPlaceholder')} required
          className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-lime-400 outline-none" />
      </div>
      {/* Campo trampa: oculto a personas y lectores de pantalla; los robots lo rellenan. */}
      <div className="hidden" aria-hidden="true">
        <label htmlFor={`${idEmail}-web`}>Web</label>
        <input id={`${idEmail}-web`} type="text" name="web" tabIndex={-1} autoComplete="off" value={web} onChange={(e) => setWeb(e.target.value)} />
      </div>
      <label htmlFor={idConsent} className="flex items-start gap-3 text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
        <input id={idConsent} type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-lime-500 flex-none" />
        <span>
          {t('avisame.consentA')}{' '}
          <Link to="/privacidad" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">{t('avisame.consentLink')}</Link>{' '}
          {t('avisame.consentB')}
        </span>
      </label>
      {error && <p className="text-sm text-red-600 font-medium" role="alert">{error}</p>}
      <div className="flex flex-col sm:flex-row gap-2">
        <button type="submit" disabled={loading}
          className="flex-1 py-3 rounded-2xl bg-lime-400 hover:bg-lime-300 disabled:opacity-50 text-[#0A1A2B] font-extrabold shadow-lg shadow-lime-500/20 transition-all">
          {loading ? t('avisame.loading') : t('avisame.submit')}
        </button>
        {onCerrar && (
          <button type="button" onClick={onCerrar} className="px-4 py-3 rounded-2xl text-sm font-semibold text-gray-500 hover:text-gray-700 dark:hover:text-gray-200">
            {t('avisame.cerrar')}
          </button>
        )}
      </div>
      <p className="text-[11px] text-gray-400">{t('avisame.nota')}</p>
    </form>
  );
}
